import mapboxgl from "mapbox-gl";
import { get } from "svelte/store";

import AlertsStore from "../Alerts/Store";
import AppMenuTab from "../AppMenuTab";
import AppStore from "../AppStore";
import MapStore from "../Map/Store";
import InvestigateDataGapsStore from "../UI/VesselHistory/InvestigateDataGapsStore";
import VesselHistoryStore from "../UI/VesselHistory/Store";
import VesselsNearbyStore from "../UI/VesselsNearby/Store";

import AuthStore from "../Auth/Store";

import API from "../API/API";
import ROICache from "../Helpers/ROICache";
import RegionType from "../Models/Enums/RegionType";

import CustomMapDatasets from "./CustomMapDatasets";
import { Layout, Paint } from "./MapLayerStyles";

import Feature from "../Branding/Feature";
import User from "../Branding/User";

import ROIPopupManager from "./ROIPopupManager";

import GeoHelper from "../Helpers/GeoHelper";

import MapControlType from "./Enums/MapControlType";
import MapDataSource from "./Enums/MapDataSource";
import MapLayer from "./Enums/MapLayer";
import MapViewType from "./Enums/MapViewType";
import PopupStore from "./Popup/PopupStore";

import LoadState from "../Enums/LoadState";
import SatCapStore from "../Environment/SatCap/Store";
import AnnotationsStore from "../UI/Annotations/Store";
import FocusVessel from "../UI/Icons/focus-vessel.png";
import IconWarning32 from "../UI/Icons/warning-32.png";
import VisibilityState from "./Enums/VisibilityState";

import AircraftHistoryStore from "../Aviation/AircraftHistoryStore";
import SatRFStore from "../Environment/SatRF/Store";
import EditCustomGeoJSONStore from "../UI/CustomGeoJSONOverlayForm/EditStore";
import ShapeType from "../UI/CustomGeoJSONOverlayForm/ShapeType";
import RoutePlottingStore from "../UI/MeasureTool/RoutePlottingStore";
import CablesStore from "../UI/NationalGrid/CablesStore";
import SatelliteViewStore from "../UI/SatelliteView/Store";
import MapLayerGroup from "./Enums/MapLayerGroup";

const MapLayerManager = {};

MapLayerManager.setLayoutPropertyIfExists = (layerName, visibilityState) => {
	const map = get(MapStore.mapbox.instance);
	const layer = map.getLayer(layerName);
	if (layer) {
		map.setLayoutProperty(layerName, "visibility", visibilityState);
	}
};
MapLayerManager.updateVisibleLayers = () => {
	const map = get(MapStore.mapbox.instance);
	const initialised = get(MapStore.meta.initialised);
	const infoMarkers = get(MapStore.rois.infoMarkers);
	if (map && initialised) {
		for (const key in MapLayer) {
			MapLayerManager.setLayoutPropertyIfExists(MapLayer[key], VisibilityState.NONE);
		}

		// TODO: This could certainly be done better...  We should wait and see how the UI & datasets evolve over time.
		AlertsStore.vesselGroup.visible.set(false);
		VesselHistoryStore.vesselGroup.visible.set(false);
		VesselsNearbyStore.results.vesselGroup.visible.set(false);

		SatelliteViewStore.actions.update(map);

		const view = get(MapStore.view);
		if (view === MapViewType.ALERTS) {
			AlertsStore.vesselGroup.visible.set(true);
			MapLayerManager.setLayoutPropertyIfExists(MapLayer.VESSELS_LIVE, VisibilityState.VISIBLE);
			MapLayerManager.setLayoutPropertyIfExists(MapLayer.VESSELS_HISTORY_LINES, VisibilityState.VISIBLE);
			MapLayerManager.setLayoutPropertyIfExists(MapLayer.VESSELS_HISTORY_POINTS, VisibilityState.VISIBLE);
			MapLayerManager.setLayoutPropertyIfExists(MapLayer.VESSELS_LIVE_NEARBY_BOUNDS, VisibilityState.VISIBLE);
		}
		else if (view === MapViewType.FLEET_HISTORY) {
			VesselHistoryStore.vesselGroup.visible.set(true);
			MapLayerManager.setLayoutPropertyIfExists(MapLayer.HEATMAP, (get(VesselHistoryStore.filters.layers.heatmap.enabled) ? VisibilityState.VISIBLE : VisibilityState.NONE));
			MapLayerManager.setLayoutPropertyIfExists(MapLayer.HEATMAP_POINTS, (get(VesselHistoryStore.filters.layers.positions.enabled) ? VisibilityState.VISIBLE : VisibilityState.NONE));
			MapLayerManager.setLayoutPropertyIfExists(MapLayer.HEATMAP_LINES, (get(VesselHistoryStore.filters.layers.journeys.enabled) ? VisibilityState.VISIBLE : VisibilityState.NONE));
			MapLayerManager.setLayoutPropertyIfExists(MapLayer.VESSEL_HISTORY_SNAPSHOT, (get(VesselHistoryStore.filters.layers.vessels.enabled) ? VisibilityState.VISIBLE : VisibilityState.NONE));

			const highlightOn = get(InvestigateDataGapsStore.enabled) && User.hasFeature(Feature.VESSEL_HISTORY.INVESTIGATE);
			MapLayerManager.setLayoutPropertyIfExists(MapLayer.HIGHLIGHT_LINES, (highlightOn ? VisibilityState.VISIBLE : VisibilityState.NONE));
		}
		else if (view === MapViewType.NEARBY_VESSELS) {
			VesselsNearbyStore.results.vesselGroup.visible.set(true);
			MapLayerManager.setLayoutPropertyIfExists(MapLayer.VESSELS_NEARBY, VisibilityState.VISIBLE);
			MapLayerManager.setLayoutPropertyIfExists(MapLayer.VESSELS_NEARBY_LINES, VisibilityState.VISIBLE);
		}
		map.setLayoutProperty(MapLayer.VESSEL_FOCUSED, "visibility", VisibilityState.VISIBLE);

		infoMarkers.forEach((marker) => {
			if (view === MapViewType.ALERTS) {
				if (!marker._map) { // Filthy hack!
					marker.addTo(map);
				}
			}
			else {
				marker.remove();
			}
		});

		const measureOn = get(RoutePlottingStore.meta.visible);
		MapLayerManager.setLayoutPropertyIfExists(MapLayer.MEASURE_POINTS, (measureOn ? VisibilityState.VISIBLE : VisibilityState.NONE));
		MapLayerManager.setLayoutPropertyIfExists(MapLayer.MEASURE_POINT_LINES, (measureOn ? VisibilityState.VISIBLE : VisibilityState.NONE));
		MapLayerManager.setLayoutPropertyIfExists(MapLayer.MEASURE_LABELS, (measureOn ? VisibilityState.VISIBLE : VisibilityState.NONE));

		MapLayerManager.setLayoutPropertyIfExists(MapLayer.ROUTEPLOT_POINTS, (measureOn ? VisibilityState.VISIBLE : VisibilityState.NONE));
		MapLayerManager.setLayoutPropertyIfExists(MapLayer.ROUTEPLOT_POINT_LINES, (measureOn ? VisibilityState.VISIBLE : VisibilityState.NONE));
		MapLayerManager.setLayoutPropertyIfExists(MapLayer.ROUTEPLOT_LABELS, (measureOn ? VisibilityState.VISIBLE : VisibilityState.NONE));

		// Sanctions
		const sanctionsVisible = get(MapStore.sanctionsVisible);
		MapLayerManager.setLayoutPropertyIfExists(MapLayer.SANCTIONS_POLYGONS, (sanctionsVisible ? VisibilityState.VISIBLE : VisibilityState.NONE));

		// Aviation
		const aircraftFakeVisible = get(AppStore.menu.tab) === AppMenuTab.AVIATION && get(AppStore.menu.aviation.tab) === "Aircraft (Sample)";
		MapLayerManager.setLayoutPropertyIfExists(MapLayer.AIRCRAFT_FAKE_LIVE, (aircraftFakeVisible ? VisibilityState.VISIBLE : VisibilityState.NONE));
		MapLayerManager.setLayoutPropertyIfExists(MapLayer.AIRCRAFT_FAKE_HISTORIC_LINES, (aircraftFakeVisible ? VisibilityState.VISIBLE : VisibilityState.NONE));
		MapLayerManager.setLayoutPropertyIfExists(MapLayer.AIRCRAFT_FAKE_HISTORIC_POINTS, (aircraftFakeVisible ? VisibilityState.VISIBLE : VisibilityState.NONE));

		const aircraftAlertsVisible = get(AppStore.menu.tab) === AppMenuTab.AVIATION && get(AppStore.menu.aviation.tab) === "Alerts";
		MapLayerManager.setLayoutPropertyIfExists(MapLayer.AIRCRAFT_ALERTS, (aircraftAlertsVisible ? VisibilityState.VISIBLE : VisibilityState.NONE));

		const aircraftHistoryVisible = get(AppStore.menu.tab) === AppMenuTab.AVIATION && get(AppStore.menu.aviation.tab) === "History";
		MapLayerManager.setLayoutPropertyIfExists(MapLayer.AIRCRAFT_HISTORY_SNAPSHOT, (aircraftHistoryVisible && get(AircraftHistoryStore.filters.layers.aircraft.enabled) ? VisibilityState.VISIBLE : VisibilityState.NONE));
		MapLayerManager.setLayoutPropertyIfExists(MapLayer.AIRCRAFT_HISTORY_POINTS, (aircraftHistoryVisible && get(AircraftHistoryStore.filters.layers.positions.enabled) ? VisibilityState.VISIBLE : VisibilityState.NONE));
		MapLayerManager.setLayoutPropertyIfExists(MapLayer.AIRCRAFT_HISTORY_LINES, (aircraftHistoryVisible && get(AircraftHistoryStore.filters.layers.journeys.enabled) ? VisibilityState.VISIBLE : VisibilityState.NONE));
		MapLayerManager.setLayoutPropertyIfExists(MapLayer.AIRCRAFT_HISTORY_HEATMAP, (aircraftHistoryVisible && get(AircraftHistoryStore.filters.layers.heatmap.enabled) ? VisibilityState.VISIBLE : VisibilityState.NONE));

		// Environment
		const environmentVisibilityState = (get(AppStore.menu.tab) === AppMenuTab.ENVIRONMENT) ? VisibilityState.VISIBLE : VisibilityState.NONE;
		MapLayerManager.setLayoutPropertyIfExists(MapLayer.ENVIRONMENT_LINES, environmentVisibilityState);
		MapLayerManager.setLayoutPropertyIfExists(MapLayer.ENVIRONMENT_LABELS, environmentVisibilityState);

		// Custom layers
		const annotationsVisibilityState = get(AnnotationsStore.meta.visible) ? VisibilityState.VISIBLE : VisibilityState.NONE;
		map.setLayoutProperty(MapLayer.CUSTOM_GEOJSON_FILLS, 		"visibility", annotationsVisibilityState);
		map.setLayoutProperty(MapLayer.CUSTOM_GEOJSON_LINES, 		"visibility", annotationsVisibilityState);
		map.setLayoutProperty(MapLayer.CUSTOM_DRAW_LINES, 			"visibility", annotationsVisibilityState);
		map.setLayoutProperty(MapLayer.CUSTOM_DRAW_LINES_POINTS, 	"visibility", annotationsVisibilityState);
		map.setLayoutProperty(MapLayer.CUSTOM_DRAW_LINES_FILLS, 	"visibility", annotationsVisibilityState);

		// sat cap
		const satcapVisibilityState = get(SatCapStore.meta.visible) ? VisibilityState.VISIBLE : VisibilityState.NONE;
		MapLayerManager.setLayoutPropertyIfExists(MapLayer.MDA_SATCAP, satcapVisibilityState);

		// sat RF
		const satrfVisibilityState = get(SatRFStore.meta.visible) ? VisibilityState.VISIBLE : VisibilityState.NONE;
		MapLayerManager.setLayoutPropertyIfExists(MapLayer.CLEOS_SATRF_VESSELS, satrfVisibilityState);
		MapLayerManager.setLayoutPropertyIfExists(MapLayer.CLEOS_SATRF, satrfVisibilityState);

		// national grid
		const cableLinesVisibilityState = get(CablesStore.meta.visible) && get(CablesStore.meta.visibility.lines) ? VisibilityState.VISIBLE : VisibilityState.NONE;
		const cablePointsVisibilityState = get(CablesStore.meta.visible) && get(CablesStore.meta.visibility.points) ? VisibilityState.VISIBLE : VisibilityState.NONE;
		const cableBufferVisibilityState = get(CablesStore.meta.visible) && get(CablesStore.meta.visibility.buffers) ? VisibilityState.VISIBLE : VisibilityState.NONE;
		MapLayerManager.setLayoutPropertyIfExists(MapLayer.NATIONALGRID_UNDERSEA_CABLE_LINES, cableLinesVisibilityState);
		MapLayerManager.setLayoutPropertyIfExists(MapLayer.NATIONALGRID_UNDERSEA_CABLE_BUFFERS, cableBufferVisibilityState);
		MapLayerManager.setLayoutPropertyIfExists(MapLayer.NATIONALGRID_UNDERSEA_CABLE_POINTS, cablePointsVisibilityState);

		// Timezone Overlay
		const timezoneVisible = get(MapStore.timezoneVisible);
		const timezoneVisibility = (timezoneVisible);
		const timezoneLayerIds = [
			MapLayer.TIMEZONE,
			MapLayer.TIMEZONE_NAMES,
			MapLayer.TIMEZONE_LINES
		];
		timezoneLayerIds.forEach((layer) => {
			MapLayerManager.setLayoutPropertyIfExists(layer, (timezoneVisibility ? VisibilityState.VISIBLE : VisibilityState.NONE));
		});

		// Custom layers.
		const keys = Object.keys(CustomMapDatasets.data);
		for (const key in keys) {
			const customVisible = get(CustomMapDatasets.get(keys[key]).visible);
			map.setLayoutProperty(keys[key], "visibility", (customVisible ? VisibilityState.VISIBLE : VisibilityState.NONE));
		}
	}
};

const loadROIs = () => {
	return new Promise((resolve, reject) => {
		MapStore.rois.loadState.set(LoadState.LOADING);

		let loadingCount = 1;

		// The ROI models for the current user are saved here.
		// They are refreshed on login and every hour when the refresh token is used.
		// They don't currently contain geojson
		const rois = get(AuthStore.session.rois);

		const onLoadingCountChange = (count) => {
			if (count === 0) {
				AuthStore.session.rois.set(rois);
				AuthStore.actions.save();
				MapStore.rois.loadState.set(LoadState.LOADED);
				resolve(rois);
			}
		};

		// Check the time pulled down from login rois is different from saved rois. Update them if necessary.
		for (let i = 0; i < rois.length; i++) {
			const roi = rois[i];
			loadingCount++;
			if (ROICache.requiresUpdating(roi)) {
				// Re-download from the server.
				API.get("rois/" + roi.id).then((result) => {
					roi.geojson = result.geojson;
					roi.lastUpdated = result.last_updated;
					ROICache.put(roi);
					loadingCount--;
					onLoadingCountChange(loadingCount);
				}).catch(() => {
					// TODO: Not sure what to do in this case.
					// MapStore.rois.loadState.set(LoadState.LOADED);
				});
			}
			else {
				// Load it from the localStorage
				const cached = ROICache.get(roi);
				roi.geojson = cached.geojson;
				loadingCount--;
				onLoadingCountChange(loadingCount);
			}
		}
		loadingCount--;
		onLoadingCountChange(loadingCount);

		if (rois.length === 0) {
			onLoadingCountChange(0);
		}
	});
};

MapLayerManager._layerGroups = [];
MapLayerManager.addLayerGroup = (group, layers, sources, index = -1) => {
	const groupIndex = Object.values(MapLayerGroup).indexOf(group);

	let obj = MapLayerManager.getLayerGroup(group);
	const exists = (obj !== null);
	if (!exists) {
		obj = {};
	}

	obj.name = group;
	obj.layers = layers;
	obj.sources = sources;
	obj.index = (index === -1) ? groupIndex : index;

	if (!exists) {
		MapLayerManager._layerGroups.push(obj);
	}
	MapLayerManager.reorderLayerGroups();
};
MapLayerManager.reorderLayerGroups = () => {
	const map = get(MapStore.mapbox.instance);

	// Go from top to bottom!
	const inOrder = MapLayerManager._layerGroups.sort((a, b) => {
		return a.index - b.index;
	});

	let lastLayer = null;
	// for(let i = 0; i < MapLayerManager._layerGroups.length; i++) {
	for (let i = inOrder.length - 1; i >= 0; i--) {
		const group = inOrder[i];
		const layers = group.layers;
		// for(let j = 0; j < layers.length; j++) {
		for (let j = layers.length - 1; j >= 0; j--) {
			if (lastLayer) {
				map.moveLayer(layers[j], lastLayer);
			}
			else {
				map.moveLayer(layers[j]);
			}
			lastLayer = layers[j];
		}
	}
};
MapLayerManager.getLayerGroup = (group) => {
	for (let i = 0; i < MapLayerManager._layerGroups.length; i++) {
		if (MapLayerManager._layerGroups[i].name === group) {
			return MapLayerManager._layerGroups[i];
		}
	}
	return null;
};
MapLayerManager.removeLayerGroup = (group, removeLayersAndSources = true) => {
	const map = get(MapStore.mapbox.instance);

	for (let i = 0; i < MapLayerManager._layerGroups.length; i++) {
		if (MapLayerManager._layerGroups[i].name === group) {
			if (removeLayersAndSources) {
				for (let j = 0; j < MapLayerManager._layerGroups[i].layers.length; j++) {
					const name = MapLayerManager._layerGroups[i].layers[j];
					if (map !== null && map.getLayer(name) !== null) {
						map.removeLayer(name);
					}
				}
				for (let j = 0; j < MapLayerManager._layerGroups[i].sources.length; j++) {
					const name = MapLayerManager._layerGroups[i].sources[j];
					if (map !== null && map.getSource(name) !== null) {
						map.removeSource(name);
					}
				}
			}
			MapLayerManager._layerGroups.splice(i, 1);
			i--;
		}
	}
};

MapLayerManager.init = async mapboxInstance => {
	return new Promise((resolve, reject) => {
		const map = get(MapStore.mapbox.instance);

		// These are the default layers as defined in Mapbox studio.
		// We need to include these so the layer manager can sort our layers *underneath* the default ones, e.g. bathymetry.

		// Land
		MapLayerManager.addLayerGroup(MapLayerGroup.DEFAULT_LAND, [
			"land",
			"landcover",
			"national-park",
			"landuse"
		], []);

		// Water
		MapLayerManager.addLayerGroup(MapLayerGroup.DEFAULT_WATER, [
			"water-shadow",
			"waterway",
			"water"
		], []);

		// Buildings
		MapLayerManager.addLayerGroup(MapLayerGroup.DEFAULT_BUILDINGS, [
			"hillshade",
			"land-structure-polygon",
			"land-structure-line",
			"aeroway-polygon",
			"aeroway-line",
			"building-outline",
			"building"
		], []);

		// Tunnels
		MapLayerManager.addLayerGroup(MapLayerGroup.DEFAULT_TUNNELS, [
			"tunnel-street-minor-low",
			"tunnel-street-minor-case",
			"tunnel-primary-secondary-tertiary-case",
			"tunnel-major-link-case",
			"tunnel-motorway-trunk-case",
			"tunnel-construction",
			"tunnel-path",
			"tunnel-steps",
			"tunnel-major-link",
			"tunnel-pedestrian",
			"tunnel-street-minor",
			"tunnel-primary-secondary-tertiary",
			"tunnel-motorway-trunk"
		], []);

		// Roads
		MapLayerManager.addLayerGroup(MapLayerGroup.DEFAULT_ROADS, [
			"road-pedestrian-case",
			"road-minor-low",
			"road-street-low",
			"road-minor-case",
			"road-street-case",
			"road-secondary-tertiary-case",
			"road-primary-case",
			"road-major-link-case",
			"road-motorway-trunk-case",
			"road-construction",
			"road-path",
			"road-steps",
			"road-major-link",
			"road-pedestrian",
			"road-minor",
			"road-street",
			"road-secondary-tertiary",
			"road-primary",
			"road-motorway-trunk",
			"road-rail"
		], []);

		// Bridges
		MapLayerManager.addLayerGroup(MapLayerGroup.DEFAULT_BRIDGES, [
			"bridge-pedestrian-case",
			"bridge-street-minor-low",
			"bridge-street-minor-case",
			"bridge-primary-secondary-tertiary-case",
			"bridge-major-link-case",
			"bridge-motorway-trunk-case",
			"bridge-construction",
			"bridge-path",
			"bridge-steps",
			"bridge-major-link",
			"bridge-pedestrian",
			"bridge-street-minor",
			"bridge-primary-secondary-tertiary",
			"bridge-motorway-trunk",
			"bridge-rail",
			"bridge-major-link-2-case",
			"bridge-motorway-trunk-2-case",
			"bridge-major-link-2",
			"bridge-motorway-trunk-2"
		], []);

		// Admin boundaries
		MapLayerManager.addLayerGroup(MapLayerGroup.DEFAULT_BOUNDARIES, [
			"admin-1-boundary-bg",
			"admin-0-boundary-bg",
			"admin-1-boundary",
			"admin-0-boundary",
			"admin-0-boundary-disputed"
		], []);

		// Labels
		MapLayerManager.addLayerGroup(MapLayerGroup.DEFAULT_LABELS, [
			"road-label",
			"waterway-label",
			"natural-line-label",
			"natural-point-label",
			"water-line-label",
			"water-point-label",
			"poi-label",
			"airport-label",
			"settlement-subdivision-label",
			"settlement-label",
			"state-label",
			"country-label"
		], []);

		// Load these from local storage, or if they don't exist, or have been updated (check timestamp), pull from the API gateway/server.
		loadROIs()
			.then((allRois) => {
				const rois = allRois.filter((item) => {
					return item.regionType === RegionType.ROI;
				});
				const ttws = allRois.filter((item) => {
					return item.regionType === RegionType.TTW;
				});
				const tzs = allRois.filter((item) => {
					return item.regionType === RegionType.TZ;
				});

				const processIntoGeojson = (roisArray) => {
					let features = [];
					for (let i = 0; i < roisArray.length; i++) {
						const roi = roisArray[i];
						const geodata = JSON.parse(roi.geojson);
						if (GeoHelper.isFeatureCollection(geodata)) {
							features = [...features, ...geodata.features];
						}
						else {
							console.warn("ROI data was not a FeatureCollection", roi.id);
						}
					}
					return GeoHelper.emptyGeoJson(features);
				};

				const roisGeoJson = processIntoGeojson(rois);
				const ttwsGeoJson = processIntoGeojson(ttws);
				const tzsGeoJson = processIntoGeojson(tzs);

				map.loadImage(IconWarning32, (error, image) => {
					if (error) {
						throw error;
					}
					map.addImage("warning-32", image);
				});

				map.loadImage(FocusVessel, (error, image) => {
					if (error) {
						throw error;
					}
					map.addImage("focus-vessel", image);
				});

				map.addSource(MapDataSource.TTW_AREAS, {
					type: "geojson",
					data: ttwsGeoJson
				});
				map.addSource(MapDataSource.TZ_AREAS, {
					type: "geojson",
					data: tzsGeoJson
				});
				map.addSource(MapDataSource.ROI_AREAS, {
					type: "geojson",
					data: roisGeoJson
				});

				map.addSource(MapDataSource.VESSEL_FOCUSED, {
					type: "geojson",
					data: GeoHelper.emptyGeoJson()
				});

				// MDA SatCap

				map.addSource(MapDataSource.CUSTOM_GEOJSON, {
					type: "geojson",
					data: GeoHelper.emptyGeoJson()
				});

				map.addLayer(
					{
						id: "ttw-areas",
						type: "fill",
						source: MapDataSource.TTW_AREAS,
						layout: {
							visibility: "visible"
						},
						paint: Paint.ttw
					},
					"hillshade"
				);
				map.addLayer(
					{
						id: "traffic-zone-areas",
						type: "fill",
						source: MapDataSource.TZ_AREAS,
						layout: {
							visibility: "visible"
						},
						paint: Paint.tz
					},
					"hillshade"
				);
				map.addLayer(
					{
						id: "roi-areas",
						type: "fill",
						source: MapDataSource.ROI_AREAS,
						layout: {
							visibility: "visible"
						},
						paint: Paint.roi
					},
					"hillshade"
				);
				MapLayerManager.addLayerGroup(MapLayerGroup.KEY_AREAS, [
					"ttw-areas",
					"traffic-zone-areas",
					"roi-areas"
				], [
					MapDataSource.TTW_AREAS,
					MapDataSource.TZ_AREAS,
					MapDataSource.ROI_AREAS
				]);

				// Timezones

				// Sanctions polys

				// MDA SatCap

				map.addLayer(
					{
						id: MapLayer.VESSEL_FOCUSED,
						source: MapDataSource.VESSEL_FOCUSED,
						type: "symbol",
						layout: {
							visibility: "visible",
							"icon-image": "focus-vessel",
							"icon-size": [
								"interpolate", ["linear"],
								["zoom"],
								2, 0.25,
								6, 0.5,
								10, 1
							],
							"icon-allow-overlap": true
						}
					}
				);
				MapLayerManager.addLayerGroup(MapLayerGroup.VESSELS_SELECTED, [MapLayer.VESSEL_FOCUSED], [MapDataSource.VESSEL_FOCUSED]);

				ROIPopupManager.init(map, roisGeoJson);

				// addAlerts(map);
				// addHeatmap(map);
				// addHighlightLines(map);
				// addMeasureLayers(map);
				// addEnvironmentLayers(map);
				// addRoutePlotLayers(map);
				// addNearbyVesselsLayers(map);

				// addAviation(map);

				CustomMapDatasets.init(map);

				addCustomGeoJSONLayers(map);

				map.on("zoomend", () => {
					MapLayerManager.updateVisibleLayers();
				});
				map.on("gestureend", () => {
					MapLayerManager.updateVisibleLayers();
				});
				MapLayerManager.updateVisibleLayers();

				resolve();
			});
	});
};

const addCustomGeoJSONLayers = (map) => {
	// Custom geojson input layers.
	map.addSource(MapDataSource.CUSTOM_GEOJSON_FILLS, {
		type: "geojson",
		data: GeoHelper.emptyGeoJson()
	});
	map.addSource(MapDataSource.CUSTOM_GEOJSON_LINES, {
		type: "geojson",
		data: GeoHelper.emptyGeoJson()
	});

	map.addLayer(
		{
			id: MapLayer.CUSTOM_GEOJSON_FILLS,
			source: MapDataSource.CUSTOM_GEOJSON_FILLS,
			type: "fill",
			layout: Layout.invisible,
			paint: Paint.customFills
		},
		"hillshade"
	);

	map.addLayer(
		{
			id: MapLayer.CUSTOM_GEOJSON_LINES,
			source: MapDataSource.CUSTOM_GEOJSON_LINES,
			type: "line",
			layout: Layout.invisible,
			paint: Paint.customLines
		},
		"hillshade"
	);

	// Custom drawing layers -- these are used when adding/editing the polys/lines.
	map.addSource(MapDataSource.CUSTOM_DRAW_LINES, {
		type: "geojson",
		data: GeoHelper.emptyGeoJson()
	});
	map.addSource(MapDataSource.CUSTOM_DRAW_LINES_POINTS, {
		type: "geojson",
		data: GeoHelper.emptyGeoJson()
	});
	map.addSource(MapDataSource.CUSTOM_DRAW_LINES_FILLS, {
		type: "geojson",
		data: GeoHelper.emptyGeoJson()
	});

	// TODO: FILL type.
	// map.addLayer(
	// 	{
	// 		id: MapLayer.CUSTOM_DRAW_LINES,
	// 		source: MapDataSource.CUSTOM_DRAW_LINES,
	// 		type: "line",
	// 		layout: Layout.invisible,
	// 		paint: Paint.customLines
	// 	},
	// 	"hillshade"
	// );
	map.addLayer(
		{
			id: MapLayer.CUSTOM_DRAW_LINES_FILLS,
			source: MapDataSource.CUSTOM_DRAW_LINES_FILLS,
			type: "fill",
			layout: Layout.invisible,
			paint: Paint.customFills
		},
		"hillshade"
	);
	map.addLayer(
		{
			id: MapLayer.CUSTOM_DRAW_LINES,
			source: MapDataSource.CUSTOM_DRAW_LINES,
			type: "line",
			layout: Layout.invisible,
			paint: Paint.customLines
		},
		"hillshade"
	);
	map.addLayer(
		{
			id: MapLayer.CUSTOM_DRAW_LINES_POINTS,
			source: MapDataSource.CUSTOM_DRAW_LINES_POINTS,
			type: "circle",
			layout: Layout.invisible,
			paint: Paint.customLinePoints
		},
		"hillshade"
	);
	MapLayerManager.addLayerGroup(MapLayerGroup.CUSTOM_LAYERS, [
		MapLayer.CUSTOM_GEOJSON_FILLS,
		MapLayer.CUSTOM_GEOJSON_LINES,
		MapLayer.CUSTOM_DRAW_LINES_FILLS,
		MapLayer.CUSTOM_DRAW_LINES,
		MapLayer.CUSTOM_DRAW_LINES_POINTS
	], [
		MapDataSource.CUSTOM_GEOJSON_FILLS,
		MapDataSource.CUSTOM_GEOJSON_LINES,
		MapDataSource.CUSTOM_DRAW_LINES,
		MapDataSource.CUSTOM_DRAW_LINES_POINTS,
		MapDataSource.CUSTOM_DRAW_LINES_FILLS
	]);

	const processProps = (inProps) => {
		const props = { ...inProps };
		if (typeof props.index !== "undefined") {
			delete props.index;
		}
		if (typeof props.color !== "undefined") {
			delete props.color;
		}
		return props;
	};
	const tryEditFeature = (event) => {
		if (get(MapStore.control) !== MapControlType.PAN) {
			return;
		}

		event.preventDefault();
		const features = map.queryRenderedFeatures(event.point, {
			layers: [MapLayer.CUSTOM_GEOJSON_FILLS, MapLayer.CUSTOM_GEOJSON_LINES]
		});
		if (features.length === 0) {
			return;
		}

		const feature = features[0];

		let shapeType = ShapeType.POINTS;
		if (feature.layer.type === "fill") {
			shapeType = ShapeType.POLYGON;
		}
		else if (feature.layer.type === "line") {
			shapeType = ShapeType.LINE;
		}

		EditCustomGeoJSONStore.shape.index.set(feature.properties.index);
		EditCustomGeoJSONStore.shape.color.set("#000000");
		if (typeof feature.properties.color === "string") {
			EditCustomGeoJSONStore.shape.color.set(feature.properties.color);
		}
		EditCustomGeoJSONStore.shape.type.set(shapeType);
		EditCustomGeoJSONStore.shape.properties.set(processProps(feature.properties));

		EditCustomGeoJSONStore.meta.visible.set(true);
		EditCustomGeoJSONStore.meta.lastUpdated.set(new Date());
	};
	const tryDeleteFeature = (event) => {
		if (get(MapStore.control) !== MapControlType.DELETE_CUSTOM_GEOJSON) {
			return;
		}

		event.preventDefault();
		const features = map.queryRenderedFeatures(event.point, {
			layers: [MapLayer.CUSTOM_GEOJSON_FILLS, MapLayer.CUSTOM_GEOJSON_LINES]
		});
		if (features.length === 0) {
			return;
		}

		const feature = features[0];
		MapLayerManager.removeCustomGeoJSONLayer(feature.properties.index);
	};
	map.on("click", MapLayer.CUSTOM_GEOJSON_FILLS, tryEditFeature);
	map.on("click", MapLayer.CUSTOM_GEOJSON_LINES, tryEditFeature);
	map.on("click", MapLayer.CUSTOM_GEOJSON_FILLS, tryDeleteFeature);
	map.on("click", MapLayer.CUSTOM_GEOJSON_LINES, tryDeleteFeature);

	const showPopup = (event, shapeType) => {
		map.getCanvas().style.cursor = "pointer";
		const props = processProps(event.features[0].properties);
		const coords = [event.lngLat.lng, event.lngLat.lat]; // event.features[0].geometry.coordinates;
		let contents = "";
		contents += PopupStore.helpers.generateKeyValueList(props);
		contents += "<i>Click to edit</i>";

		PopupStore.actions.customHtml(contents, 250, coords, true);
	};
	const hidePopup = (event) => {
		map.getCanvas().style.cursor = "crosshair";
		PopupStore.actions.custom({}, null, false);
	};
	map.on("mouseover", MapLayer.CUSTOM_GEOJSON_FILLS, (e) => {
		showPopup(e, ShapeType.POLYGON);
	});
	map.on("mousemove", MapLayer.CUSTOM_GEOJSON_FILLS, (e) => {
		showPopup(e, ShapeType.POLYGON);
	});
	map.on("mouseout", MapLayer.CUSTOM_GEOJSON_FILLS, hidePopup);

	map.on("mouseover", MapLayer.CUSTOM_GEOJSON_LINES, (e) => {
		showPopup(e, ShapeType.LINE);
	});
	map.on("mousemove", MapLayer.CUSTOM_GEOJSON_LINES, (e) => {
		showPopup(e, ShapeType.LINE);
	});
	map.on("mouseout", MapLayer.CUSTOM_GEOJSON_LINES, hidePopup);

	const tryFinishFeature = (event) => {

	};

	map.on("click", MapLayer.CUSTOM_GEOJSON_FILLS, tryFinishFeature);
	map.on("click", MapLayer.CUSTOM_GEOJSON_LINES, tryFinishFeature);
};
// let customIndex = 0;
MapLayerManager.addCustomGeoJSONLayer = (featureCollectionString) => {
	let featureCollection;
	try {
		featureCollection = JSON.parse(featureCollectionString);
	}
	catch (e) {
		console.warn("Invalid GeoJSON.");
		return false;
	}
	if (!GeoHelper.isFeatureCollection(featureCollection)) {
		console.warn("Input must be a FeatureCollection.");
		return false;
	}
	if (featureCollection.features.length === 0) {
		// console.warn("FeatureCollection must contain at least one feature.");
		return true;
	}

	if (typeof featureCollection.properties === "undefined") {
		featureCollection.properties = {};
	}

	const currentLayers = get(MapStore.controlData.customGeojson.layers.input);
	featureCollection.properties.index = currentLayers.length;
	currentLayers.push(featureCollection);
	MapStore.controlData.customGeojson.layers.input.set(currentLayers);

	return true;
};
MapLayerManager.getNewestCustomGeoJSONLayer = () => {
	const layers = get(MapStore.controlData.customGeojson.layers.input);
	return layers[layers.length - 1];
};
MapLayerManager.removeCustomGeoJSONLayer = (index) => {
	let found = false;
	const currentLayers = get(MapStore.controlData.customGeojson.layers.input); // .. ?
	for (let i = 0; i < currentLayers.length; i++) {
		if (currentLayers[i].properties.index === index) {
			found = true;
			currentLayers.splice(i, 1);
			break;
		}
	}
	if (!found) {
		return false;
	}
	MapStore.controlData.customGeojson.layers.input.set(currentLayers);
	return true;
};

MapLayerManager.markerInstances = [];
MapLayerManager.clearAnnotations = () => {
	// Clear lines/polys
	MapStore.controlData.customGeojson.layers.input.set([]);
	MapStore.controlData.draw.points.set([]);
};
MapLayerManager.clearCustomMarkers = (clearData = false) => {
	// Clear markers
	for (let i = 0; i < MapLayerManager.markerInstances.length; i++) {
		MapLayerManager.markerInstances[i].remove();
	}
	MapLayerManager.markerInstances = [];
	if (clearData) {
		MapStore.controlData.draw.markers.set([]);
	}
};
MapLayerManager.updateCustomMarkers = () => {
	const map = get(MapStore.mapbox.instance);
	const markers = get(MapStore.controlData.draw.markers);
	MapLayerManager.clearCustomMarkers();

	const annotationsVisibilityState = get(AnnotationsStore.meta.visible) ? VisibilityState.VISIBLE : VisibilityState.NONE;
	if (annotationsVisibilityState === VisibilityState.NONE) {
		return;
	}

	const processProps = (props = {}) => {
		const newProps = (typeof props !== "undefined")
			? { ...props }
			: {};
		delete newProps.lng;
		delete newProps.lat;
		if (typeof newProps.index !== "undefined") {
			delete newProps.index;
		}
		if (typeof newProps.color !== "undefined") {
			delete newProps.color;
		}
		return newProps;
	};
	const showPopup = (marker, event, shapeType) => {
		map.getCanvas().style.cursor = "pointer";
		const props = processProps(marker.source.properties);
		const lngLat = get(MapStore.cursor);
		const coords = [lngLat.lng, lngLat.lat];
		let contents = "";
		contents += PopupStore.helpers.generateKeyValueList(props);
		contents += "<i>Click to edit</i>";

		PopupStore.actions.customHtml(contents, 250, coords, true);
	};
	const hidePopup = (event) => {
		map.getCanvas().style.cursor = "crosshair";
		PopupStore.actions.custom({}, null, false);
	};

	for (let i = 0; i < markers.length; i++) {
		// make a marker for each feature and add to the map
		const inParams = {};
		if (typeof markers[i].properties !== "undefined" && typeof markers[i].properties.color === "string") {
			inParams.color = markers[i].properties.color;
		}

		const marker = new mapboxgl.Marker(inParams);
		marker.source = markers[i];
		marker.i = i;
		marker.setLngLat([markers[i].lng, markers[i].lat]);
		marker.addTo(map);

		const markerDiv = marker.getElement();
		markerDiv.addEventListener("mouseenter", (e) => {
			showPopup(marker, e, ShapeType.POINTS);
		});
		markerDiv.addEventListener("mousemove", (e) => {
			showPopup(marker, e, ShapeType.POINTS);
		});
		markerDiv.addEventListener("mouseleave", (e) => {
			hidePopup(e);
		});
		// marker.
		// marker.setPopup(popup);
		// let text = "";
		// popup.setHTML(text);
		// popup.closeOnMove = true;

		marker._element.addEventListener("click", () => {
			if (get(MapStore.control) === MapControlType.PAN) {
				EditCustomGeoJSONStore.shape.index.set(i);
				EditCustomGeoJSONStore.shape.color.set("#000000");
				if (typeof marker.source.properties !== "undefined" &&
					typeof marker.source.properties.color === "string") {
					EditCustomGeoJSONStore.shape.color.set(marker.source.properties.color);
				}
				EditCustomGeoJSONStore.shape.type.set(ShapeType.POINTS);
				EditCustomGeoJSONStore.shape.properties.set(processProps(marker.source.properties));

				EditCustomGeoJSONStore.meta.visible.set(true);
				EditCustomGeoJSONStore.meta.lastUpdated.set(new Date());
			}
			else if (get(MapStore.control) === MapControlType.DELETE_CUSTOM_GEOJSON) {
				MapLayerManager.markerInstances.splice(MapLayerManager.markerInstances.indexOf(marker), 1);
				marker.remove();
				markers.splice(marker.i, 1);
				MapStore.controlData.draw.markers.set(markers);
			}
		});

		MapLayerManager.markerInstances.push(marker);
	}
};

export default MapLayerManager;
